import AfricaNavbar from "@/components/AfricaNavbar";
import AfricaFooter from "@/components/AfricaFooter";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Fingerprint, KeyRound, Lock, FileText, Database, Handshake,
  Landmark, Building2, Factory, Truck, Home, Ship, Briefcase, Award,
  Globe, ScrollText, Cpu, Search, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * THOM Verify™ Trust Centre.
 * Enterprise transparency page for governments, banks, insurers and regulators.
 * Positioning tier: Microsoft Azure Trust Center · AWS Security · Stripe Security.
 */
const PILLARS = [
  { Icon: ShieldCheck, t: "Enterprise Security",       b: "TLS in transit, at-rest encryption, HTTP security headers, rate-limited public endpoints, role-scoped APIs, immutable audit trail, principle of least privilege." },
  { Icon: Search,      t: "Verification Standards",    b: "Every verification follows a documented 10-stage lifecycle. Evidence is captured with chain-of-custody metadata (uploader, timestamp, cryptographic hash) before it can be relied on." },
  { Icon: ScrollText,  t: "Digital Passports",         b: "Every verification generates a permanent TVP-AFR-YYYY-NNNNNN identifier — a durable, hash-anchored record independent of the underlying case number." },
  { Icon: Award,       t: "Trust Certificates",        b: "Tamper-evident, QR-verified PDF certificates. Signed today with HMAC-SHA256; upgrade path in place for full X.509 PKI once HSM is provisioned." },
  { Icon: KeyRound,    t: "Escrow Protection",         b: "THOM Escrow™ is decoupled from the payment gateway. Funds only release on verified milestones — buyer acceptance, admin approval, or a neutral-arbiter workflow." },
  { Icon: Fingerprint, t: "Digital Signatures",        b: "Every certificate carries a deterministic content hash. Any tampering — including a single-character change — is detectable at re-verification time." },
  { Icon: FileText,    t: "Independent Assurance",     b: "THOM Verify complements — never replaces — statutory authorities, regulators, law enforcement, engineers acting in a certification capacity, insurers and legal advisers." },
  { Icon: Lock,        t: "Privacy & Data Protection", b: "Sensitive fields are never exposed publicly. Public tracking pages surface only status metadata. Internal notes, evidence, prices and third-party contacts are never disclosed to unauthorised parties." },
  { Icon: Database,    t: "Evidence Vault",            b: "Chain-of-custody storage for photographs, deeds, invoices, contracts, technical records and identity artefacts. Storage URLs are never exposed to the client." },
  { Icon: Cpu,         t: "Fraud Prevention",          b: "Automated risk scoring at intake · immutable audit trail · rate-limited tracking · generic invalid-reference responses to prevent enumeration attacks." },
  { Icon: Globe,       t: "Africa Coverage",           b: "One shared trust engine deployed across 14 African markets. Country-specific registrars, police databases, currencies and inspector networks configured per deployment." },
  { Icon: Handshake,   t: "Compliance",                b: "Institutional-grade audit records support downstream regulatory review. Every mutation carries actor · action · target · timestamp · prev/new value in an immutable ledger." },
];

const PARTNERS = [
  { Icon: Landmark,   t: "Governments & Regulators" },
  { Icon: Building2,  t: "Banks & Financial Institutions" },
  { Icon: ShieldCheck,t: "Insurers" },
  { Icon: Factory,    t: "Mining & Industrial" },
  { Icon: Truck,      t: "Fleet Operators" },
  { Icon: Home,       t: "Real-estate Institutions" },
  { Icon: Ship,       t: "Cross-border Trade" },
  { Icon: Briefcase,  t: "Corporate Procurement" },
];

const FAQ = [
  { q: "Does THOM Verify replace regulators, police, insurers or courts?",
    a: "No. THOM Verify complements them. Our findings are independent, evidence-based and issued at the moment of verification. Final regulatory decisions remain the responsibility of the appropriate authorities." },
  { q: "How does the public tracker keep sensitive data private?",
    a: "The tracker exposes only sanitised status metadata (reference, country, category, stage, progress, next action, assigned team). Internal notes, evidence, prices, investigator identities, third-party contacts and storage URLs are never shown to the public. Every lookup is rate-limited and audit-logged." },
  { q: "How is a certificate authenticated?",
    a: "Every certificate PDF carries a QR code, a certificate number and a deterministic SHA-256 hash of its material fields. Re-computing the hash on the server confirms the certificate has not been altered." },
  { q: "Which African countries are supported?",
    a: "Zimbabwe is live for commercial verification (vehicles, property, sourcing, marketplace, escrow, digital passports). South Africa, Botswana, Zambia, Namibia, Mozambique, Kenya, Tanzania, Uganda, Rwanda, Nigeria, Ghana, DRC, Egypt and Morocco accept enterprise verification requests today, with country-portal launches following as data partnerships come online." },
  { q: "How are payments protected?",
    a: "THOM Escrow™ holds funds independently of any payment provider. Release triggers are verification milestones (inspection complete, legal verified, buyer accepts) or a neutral admin arbiter — never the seller acting alone." },
  { q: "What happens if my file upload is potentially malicious?",
    a: "Uploads pass through content-type and size validation at ingest. Deployment-tier customers should enable ClamAV, Cloudmersive or VirusTotal scanning — the MediaService quarantine hook is already in place for automated rejection and admin alerts." },
];

export default function TrustCentre() {
  const [cert, setCert] = useState("");
  const nav = useNavigate();
  const go = () => { const q = cert.trim(); if (q) nav(`/verify/${encodeURIComponent(q)}`); };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <AfricaNavbar />
      <section className="max-w-[1500px] mx-auto px-6 pt-16 pb-8 lg:pt-24">
        <div className="max-w-[820px]">
          <div className="kicker text-white/60">THOM Verify™ · Trust Centre</div>
          <h1 className="display text-4xl lg:text-6xl mt-3 leading-[1.02]" data-testid="trust-title">
            Enterprise-grade trust.<br/>Transparent by design.
          </h1>
          <p className="mt-6 text-white/75 text-[15.5px] leading-[1.85] max-w-[720px]">
            THOM Verify™ is Africa's Independent Verification and Transaction Assurance Company.
            This page documents the security, verification and privacy commitments behind every
            certificate we issue, every request we track and every asset we passport.
          </p>
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 py-8" data-testid="trust-pillars">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {PILLARS.map((p) => (
            <div key={p.t} className="border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors p-6" data-testid={`trust-pillar-${p.t.replace(/\W+/g,'-').toLowerCase()}`}>
              <p.Icon className="h-5 w-5 text-[#B08C4A]" />
              <h3 className="mt-4 text-[16px] font-semibold">{p.t}</h3>
              <p className="mt-2 text-[13px] text-white/70 leading-[1.7]">{p.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Certificate verify inline */}
      <section className="max-w-[1500px] mx-auto px-6 py-12" data-testid="trust-verify-band">
        <div className="border border-white/15 bg-white/[0.03] p-8 lg:p-12 grid lg:grid-cols-[1.2fr_1fr] gap-8 items-center">
          <div>
            <div className="kicker text-white/70">Verify a certificate</div>
            <h3 className="display text-2xl lg:text-3xl mt-2">Confirm any THOM Verify™ certificate in seconds.</h3>
            <p className="mt-3 text-white/70 text-[14px] leading-[1.7] max-w-[540px]">
              Enter a certificate number, digital passport ID or verification reference. Public
              authentication requires no login.
            </p>
          </div>
          <div>
            <label className="mono text-[10.5px] uppercase tracking-[0.22em] text-white/50">Reference</label>
            <div className="mt-2 flex gap-2">
              <Input value={cert} onChange={(e) => setCert(e.target.value)} placeholder="TVC-2026-XXXX-XXXX · TV-AFR-XXXXXXXX · TVP-AFR-YYYY-NNNNNN" className="bg-white/5 border-white/20 text-white placeholder:text-white/40 h-11" data-testid="trust-verify-input" onKeyDown={(e) => e.key === "Enter" && go()} />
              <Button onClick={go} className="bg-[#B08C4A] hover:bg-[#8f6f34] text-black h-11" data-testid="trust-verify-submit">Verify</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="max-w-[1500px] mx-auto px-6 py-12" data-testid="trust-partners">
        <div className="max-w-[820px] mb-6">
          <div className="kicker text-white/60">Enterprise partners we serve</div>
          <h2 className="display text-3xl mt-2">Institutions building on THOM Verify™.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {PARTNERS.map((p) => (
            <div key={p.t} className="border border-white/10 bg-white/[0.02] p-5" data-testid={`trust-partner-${p.t.replace(/\W+/g,'-').toLowerCase()}`}>
              <p.Icon className="h-4 w-4 text-[#B08C4A]" />
              <div className="mt-3 text-[14px] font-medium">{p.t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[1500px] mx-auto px-6 py-12" data-testid="trust-faq">
        <div className="max-w-[820px] mb-6">
          <div className="kicker text-white/60">Frequently asked questions</div>
          <h2 className="display text-3xl mt-2">The most common questions we answer.</h2>
        </div>
        <div className="max-w-[900px] space-y-3">
          {FAQ.map((f, i) => (
            <details key={i} className="border border-white/10 bg-white/[0.02] p-5 open:bg-white/[0.04] transition-colors" data-testid={`trust-faq-${i}`}>
              <summary className="cursor-pointer text-[14.5px] font-medium text-white marker:text-[#B08C4A] flex items-center justify-between gap-2">{f.q}<ChevronRight className="h-4 w-4 text-white/40 shrink-0" /></summary>
              <p className="mt-3 text-[13.5px] text-white/70 leading-[1.75]">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1500px] mx-auto px-6 py-12">
        <div className="border border-white/10 bg-white/[0.02] p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center gap-6" data-testid="trust-cta">
          <div className="flex-1">
            <h3 className="text-2xl font-semibold">Talk to our enterprise team.</h3>
            <p className="text-[13.5px] text-white/70 mt-2">Governments, regulators, banks, insurers, industrials, real-estate institutions and cross-border trade platforms — one form, one team.</p>
          </div>
          <Link to="/africa/enterprise#request"><Button size="lg" className="bg-[#B08C4A] hover:bg-[#8f6f34] text-black">Request Enterprise Verification <ChevronRight className="ml-1.5 h-4 w-4" /></Button></Link>
        </div>
      </section>

      <AfricaFooter />
    </div>
  );
}
